// Subquery-scope walker: given a cursor inside a statement, returns the chain
// of nested subquery/cte-def frames enclosing that cursor, ordered outermost
// first. resolveScope then materializes each frame into a Scope, parent-linked
// to its enclosing Scope.
//
// PRD: Ralph/SemanticResolverReadOnly/PRD.md § Slice: Scope Resolver —
// subqueries + pseudo-tables + resolveScope assembly.
//
// Algorithm (no tree-sitter):
//   1. Validate cursor lies within the statement range.
//   2. Compute CTE bindings for the statement — if the cursor is inside any
//      cte's bodyRange, record that as an outer cte-def frame.
//   3. Scan tokens in the active region (either the statement body OR the
//      inside of a CTE body), tracking paren depth and skipping strings /
//      comments / quoted identifiers. When a `(` is encountered at depth 0:
//        a. Find the matching `)` via skipParen.
//        b. If the cursor is inside `(…)` AND the content starts with
//           SELECT / WITH / VALUES (after whitespace/comments), push a
//           subquery frame.
//        c. Recurse: set the active region to the inside of that paren,
//           continue scanning there.
//   4. When cursor is reached or no deeper frame qualifies, stop and return
//      the accumulated chain outermost-first.
//
// We DO NOT require the immediate prefix keyword (FROM/JOIN/IN/EXISTS/…) to
// classify a `(SELECT …)` as a subquery — the "starts with SELECT/WITH"
// content check is sufficient for scope purposes. A bare expression subquery
// like `WHERE x IN (SELECT …)` still gets its own scope (its nested aliases
// don't leak out), which matches the Scope contract.

import type { SqlRange } from '../../sql-grammar/model';
import { buildLineIndex, rangeFromOffsets } from '../../sql-grammar/document/lineIndex';

import type { ParsedBuffer, ScopeKind } from '../types';
import { skipContextFor, trySkipNonCode, type SkipContext } from '../classify/tokenSkip';
import { skipParen } from './aliasScope.scanner';
import { computeCteBindings } from './cteScope';
import { frameIsLateralOrApply } from './lateralFrame';

export interface SubqueryFrame {
  readonly kind: ScopeKind;  // 'subquery' | 'cte-def'
  readonly bodyRange: SqlRange;
  // Inner range excludes the surrounding parens (empty for trivial `()`).
  readonly innerStart: number;
  readonly innerEnd: number;
  // True when this `(…)` is a LATERAL / CROSS|OUTER APPLY derived-table body —
  // resolveScope then restricts its parent-visible aliases to FROM items
  // declared before the frame (decision 3). Only set for 'subquery' frames.
  readonly lateral?: boolean;
}

interface ScanCtx {
  readonly source: string;
  readonly ctx: SkipContext;
  readonly cursorOffset: number;
  readonly end: number;
}

// Pick-subquery-at-cursor: walks one region and returns the outermost `(…)`
// whose matched close-paren encloses the cursor AND whose content is a
// subquery (SELECT/WITH/VALUES). Null if none applies.
function pickSubqueryAt(sc: ScanCtx, regionStart: number, regionEnd: number): SubqueryFrame | null {
  const { source, ctx, cursorOffset } = sc;
  let i = regionStart;

  while (i < regionEnd) {
    const skipped = trySkipNonCode(source, i, regionEnd, ctx);
    if (skipped >= 0) { i = skipped; continue; }
    const c = source[i];
    if (c !== '(') { i++; continue; }

    const closeIdx = skipParen(source, i, regionEnd, ctx);
    const containsCursor = cursorOffset >= i && cursorOffset < closeIdx;
    if (!containsCursor) { i = closeIdx; continue; }

    const innerStart = i + 1;
    const innerEnd = closeIdx - 1;
    if (isSubqueryContent(sc, innerStart, innerEnd)) {
      return {
        kind: 'subquery',
        bodyRange: { startOffset: i, endOffset: closeIdx, startLine: 0, startColumn: 0, endLine: 0, endColumn: 0 },
        innerStart,
        innerEnd,
      };
    }
    // Not subquery-shaped: descend textually to find inner subqueries (e.g.
    // a parenthesized expression containing a scalar subquery).
    i = innerStart;
  }
  return null;
}

// Content starts with SELECT / WITH / VALUES / TABLE after whitespace+comments.
function isSubqueryContent(sc: ScanCtx, start: number, end: number): boolean {
  const { source, ctx } = sc;
  let i = start;
  while (i < end) {
    const c = source[i];
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r') { i++; continue; }
    if (c === '-' && source[i + 1] === '-') {
      while (i < end && source[i] !== '\n') i++; continue;
    }
    if (c === '/' && source[i + 1] === '*') {
      let j = i + 2;
      while (j < end && !(source[j] === '*' && source[j + 1] === '/')) j++;
      if (j < end) j += 2;
      i = j; continue;
    }
    if (ctx.supportsHashComment && c === '#') {
      while (i < end && source[i] !== '\n') i++; continue;
    }
    break;
  }
  const head = source.slice(i, Math.min(i + 6, end)).toUpperCase();
  return head.startsWith('SELECT')
      || head.startsWith('WITH')
      || head.startsWith('VALUES')
      || head.startsWith('TABLE');
}

export function findSubqueryFramesAt(
  buffer: ParsedBuffer,
  statementId: string,
  cursorOffset: number,
): SubqueryFrame[] {
  const { parsedDocument, documentStructure } = buffer;
  const { source, options } = parsedDocument;
  const stmt = documentStructure.statements.find((s) => s.id === statementId);
  if (!stmt) return [];
  if (cursorOffset < stmt.range.startOffset || cursorOffset > stmt.range.endOffset) return [];

  const ctx = skipContextFor(options);
  const lineIdx = buildLineIndex(source);
  const sc: ScanCtx = { source, ctx, cursorOffset, end: stmt.range.endOffset };

  const frames: SubqueryFrame[] = [];

  // 1) CTE bodies: if cursor is inside any CTE body, that's the outermost
  // nested frame (cte-def). Use the CTE's explicit bodyRange directly.
  const ctes = computeCteBindings(buffer, statementId);
  for (const cte of ctes) {
    const { startOffset, endOffset } = cte.bodyRange;
    if (cursorOffset >= startOffset && cursorOffset < endOffset) {
      frames.push({
        kind: 'cte-def',
        bodyRange: rangeFromOffsets(lineIdx, startOffset, endOffset),
        innerStart: startOffset + 1,
        innerEnd: endOffset - 1,
      });
      break;  // only one CTE body can enclose the cursor at this level
    }
  }

  // 2) Walk into subqueries. Starting region: if we entered a CTE body, scan
  // from just inside it; otherwise scan the whole statement.
  let regionStart = stmt.range.startOffset;
  let regionEnd = stmt.range.endOffset;
  if (frames.length > 0) {
    regionStart = frames[0].innerStart;
    regionEnd = frames[0].innerEnd;
  }

  // Iteratively descend: at each level, find the subquery enclosing the
  // cursor. If found, push and narrow the region to its inside; else stop.
  for (;;) {
    const frame = pickSubqueryAt(sc, regionStart, regionEnd);
    if (!frame) break;
    frames.push({
      kind: frame.kind,
      bodyRange: rangeFromOffsets(lineIdx, frame.bodyRange.startOffset, frame.bodyRange.endOffset),
      innerStart: frame.innerStart,
      innerEnd: frame.innerEnd,
      lateral: frameIsLateralOrApply(source, frame.bodyRange.startOffset, stmt.range.startOffset),
    });
    regionStart = frame.innerStart;
    regionEnd = frame.innerEnd;
  }

  return frames;
}

